/* ═══════════════════════════════════════════════════════════════════════════
 * RÉVISION DE GRILLE SALARIALE — un ajout daté, jamais une réécriture
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ CET ÉCRAN NE CORRIGE PAS LE PASSÉ, ET C'EST SA RAISON D'ÊTRE. Une
 *    révision est une LIGNE DATÉE DE PLUS. Les bulletins déjà émis ne sont pas
 *    recalculés, et l'historique de paie reste justifiable devant un contrôle :
 *    chaque bulletin se relit avec la valeur qui était en vigueur le mois-là.
 *    La base l'impose (trigger d'immuabilité) ; l'écran ne fait que le DIRE.
 *
 * ⚠️ MOIS + ANNÉE, PAS DE JOUR. Une grille salariale prend effet au 1er du
 *    mois : un sélecteur de jour laisserait saisir le 17, que la base refuserait
 *    (`chk_gst_date_effet_premier_du_mois`). *Un champ qui laisse saisir ce que
 *    la base refuse transforme une règle en message d'erreur.* La « date du
 *    texte de référence » est, elle, un VRAI jour — un arrêté est signé un jour
 *    précis — et elle est INFORMATIVE : elle ne décide d'aucun calcul.
 *
 * ⚠️ AUCUNE VALEUR, AUCUNE DEVISE, AUCUNE CATÉGORIE EN DUR. Les catégories
 *    viennent du référentiel DU CLIENT, la devise de son pays. Cet écran
 *    n'écrit jamais « F », ne suppose pas une grille E1-E6, et ne propose aucun
 *    montant : *un montant proposé par le logiciel devient le montant payé, et
 *    c'est l'employeur qui le signe.*
 *
 * ⚠️ « À RENSEIGNER », JAMAIS « 0 ». Un agent dont la fiche ne porte aucun
 *    salaire est affiché à part et nommé. Écrire « 0 » pour une valeur absente
 *    se lit « il ne gagne rien », et quatre écrans ont déjà menti ainsi
 *    (file 102). Le moteur de paie REFUSE ces agents en citant leur matricule ;
 *    il ne les paie pas au tarif de leur catégorie.
 *
 * ⚠️ LES CASES À COCHER NE SONT PAS DÉCORATIVES. Décocher un agent ne
 *    l'« exclut » pas passivement : l'API pose pour lui une EXCEPTION
 *    INDIVIDUELLE à son montant actuel, dans la même transaction. Sans cela, la
 *    ligne de catégorie le relèverait quand même, par la résolution du moteur.
 *    L'écran l'annonce avant d'écrire, et le compte rendu le confirme après.
 * ═══════════════════════════════════════════════════════════════════════════ */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from 'services/api';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/* La devise est celle du client. Tant qu'elle n'est pas connue, on n'écrit
 * aucun symbole : mieux vaut un nombre nu qu'un nombre dans la mauvaise unité. */
function somme(v, devise) {
  if (v === null || v === undefined) return 'à renseigner';
  return `${Number(v).toLocaleString('fr-FR')}${devise ? ` ${devise}` : ''}`;
}
function moisAnnee(d) {
  if (!d) return '—';
  const [a, m] = String(d).split('-');
  return `${MOIS[Number(m) - 1]} ${a}`;
}

export default function RevisionGrillePage() {
  const maintenant = new Date();
  const [crans, setCrans] = useState([]);
  const [cransMessage, setCransMessage] = useState('');
  const [cran, setCran] = useState('');
  const [montant, setMontant] = useState('');
  const [mois, setMois] = useState(maintenant.getMonth() + 1);
  const [annee, setAnnee] = useState(maintenant.getFullYear());
  const [dateTexte, setDateTexte] = useState('');
  const [motif, setMotif] = useState('');
  /* ⚠️ `null` = rien n'a été prévisualisé, donc rien ne peut être écrit. Toute
   *    modification du cran, du montant ou de la date le remet à `null` : un
   *    aperçu qui ne correspond plus à ce qu'on s'apprête à écrire est pire que
   *    pas d'aperçu du tout. Même verrou que l'écran de classement. */
  const [apercu, setApercu] = useState(null);
  const [coches, setCoches] = useState({});     // agent_id -> true
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [compteRendu, setCompteRendu] = useState(null);
  const [histo, setHisto] = useState([]);
  const [devise, setDevise] = useState(null);

  const invalider = () => { setApercu(null); setCoches({}); setCompteRendu(null); };

  const chargerHistorique = useCallback(async () => {
    try {
      const { data } = await api.get('/grille-salariale');
      setHisto(data.revisions || []);
      if (data.devise) setDevise(data.devise);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Historique des révisions illisible.');
    }
  }, []);

  useEffect(() => { chargerHistorique(); }, [chargerHistorique]);

  /* Les catégories sont celles du client. On lit le même référentiel que
   * l'écran de classement — deux sources pour « quelles catégories existent »
   * finiraient par divergir. */
  useEffect(() => {
    api.get('/classifications/crans')
      .then(({ data }) => { setCrans(data.crans || []); setCransMessage(data.message || ''); })
      .catch(() => setCransMessage('Référentiel des catégories illisible : aucune révision possible.'));
  }, []);

  const previsualiser = async () => {
    setEnvoi(true); setErreur(''); setCompteRendu(null); setApercu(null);
    try {
      const { data } = await api.post('/grille-salariale/apercu', {
        code_cran: cran, montant: Number(montant),
        mois_effet: Number(mois), annee_effet: Number(annee),
      });
      setApercu(data);
      if (data.devise) setDevise(data.devise);
      /* Par défaut TOUS les agents concernés sont cochés : la révision d'une
       * catégorie s'applique normalement à la catégorie entière. Décocher est
       * un geste délibéré, et il laisse une trace. */
      const c = {};
      (data.groupes.sous.agents || []).forEach((a) => { c[a.agent_id] = true; });
      setCoches(c);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Aperçu impossible.');
    } finally { setEnvoi(false); }
  };

  /* `useMemo` et non une expression nue : sans lui, ce tableau serait une
   * NOUVELLE reference a chaque rendu, et les deux `useMemo` qui en dependent se
   * recalculeraient en boucle. */
  const sous = useMemo(() => (apercu ? (apercu.groupes.sous.agents || []) : []), [apercu]);
  const nbCoches = useMemo(() => sous.filter(a => coches[a.agent_id]).length, [sous, coches]);
  const nbDecoches = sous.length - nbCoches;

  /* L'impact affiché suit les cases cochées, pas l'aperçu brut : annoncer le
   * coût de la catégorie entière alors qu'on en a décoché la moitié
   * annoncerait une dépense qui n'aura pas lieu. */
  const impactCoche = useMemo(
    () => sous.filter(a => coches[a.agent_id])
      .reduce((t, a) => t + (apercu.montant - a.actuel), 0),
    [sous, coches, apercu]
  );
  const tauxPat = apercu ? apercu.impact.taux_patronal_pct : null;

  const appliquer = async () => {
    if (!apercu) { setErreur('Prévisualiser avant d’appliquer.'); return; }
    setEnvoi(true); setErreur(''); setCompteRendu(null);
    try {
      const corps = {
        code_cran: apercu.code_cran, montant: apercu.montant,
        mois_effet: Number(mois), annee_effet: Number(annee),
        motif, date_texte_reference: dateTexte || null,
      };
      /* ⚠️ TOUT COCHÉ et UNE SÉLECTION ne sont PAS la même chose côté API, et
       *    l'écran ne doit pas les confondre : champ ABSENT = toute la
       *    catégorie, tableau = ceux-là seulement, avec exception datée pour
       *    les autres. Envoyer un tableau « complet » écrirait la même chose,
       *    mais la trace dirait « appliquée à une sélection » — et *une trace
       *    qui décrit mal le geste vaut moins que pas de trace*. */
      if (nbDecoches > 0) {
        corps.agents_appliques = sous.filter(a => coches[a.agent_id]).map(a => a.agent_id);
      }
      const { data } = await api.post('/grille-salariale', corps);
      setCompteRendu(data);
      setApercu(null); setCoches({}); setMotif('');
      await chargerHistorique();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible.');
    } finally { setEnvoi(false); }
  };

  const montantValide = montant !== '' && Number(montant) > 0;
  const peutPrevisualiser = cran && montantValide && !envoi;
  const peutEcrire = apercu && motif.trim().length > 0 && !envoi;

  const annees = [];
  for (let a = maintenant.getFullYear() - 1; a <= maintenant.getFullYear() + 2; a += 1) annees.push(a);

  return (
    <div className="gss-page">
      <div className="gss-page-header">
        <h1>Révision de la grille salariale</h1>
        <div className="gss-page-subtitle">
          Une révision est un <strong>ajout daté</strong>. Les bulletins déjà émis ne sont
          pas recalculés, et un agent payé au-dessus n’est jamais baissé.
        </div>
      </div>

      {cransMessage && (
        <div className="gss-alert" style={{ background: '#FEF3C7', color: '#92400E', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          {cransMessage}
        </div>
      )}
      {erreur && (
        <div className="gss-alert" style={{ background: '#FEE2E2', color: '#991B1B', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          {erreur}
        </div>
      )}

      {/* ── LE GESTE ──────────────────────────────────────────────────────── */}
      <div className="gss-card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="gss-field" style={{ minWidth: 320 }}>
          <label>Catégorie à réviser *</label>
          <select className="gss-input" value={cran} onChange={(e) => { setCran(e.target.value); invalider(); }}>
            {/* Aucune catégorie pré-sélectionnée, et aucun libellé en dur. */}
            <option value="">— choisir une catégorie —</option>
            {crans.map(c => (
              <option key={c.origine + c.code} value={c.code}>{c.code} — {c.libelle}</option>
            ))}
          </select>
        </div>

        <div className="gss-field" style={{ minWidth: 180 }}>
          <label>Nouveau montant *</label>
          <input
            className="gss-input" type="number" min="1" step="1"
            /* Aucun placeholder chiffré : un exemple de montant se recopie. */
            placeholder={devise ? `montant en ${devise}` : 'montant'}
            value={montant}
            onChange={(e) => { setMontant(e.target.value); invalider(); }}
          />
        </div>

        {/* ⚠️ MOIS + ANNÉE. Pas de jour : l'effet est au 1er, toujours. */}
        <div className="gss-field" style={{ minWidth: 150 }}>
          <label>Mois d’effet *</label>
          <select className="gss-input" value={mois} onChange={(e) => { setMois(e.target.value); invalider(); }}>
            {MOIS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div className="gss-field" style={{ minWidth: 110 }}>
          <label>Année d’effet *</label>
          <select className="gss-input" value={annee} onChange={(e) => { setAnnee(e.target.value); invalider(); }}>
            {annees.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div className="gss-field" style={{ minWidth: 190 }}>
          <label>Date du texte de référence</label>
          <input className="gss-input" type="date" value={dateTexte} onChange={e => setDateTexte(e.target.value)} />
          <small style={{ color: '#6B7280' }}>
            Facultative. Le jour de signature de l’accord ou de l’arrêté — elle n’entre
            dans aucun calcul.
          </small>
        </div>

        <button className="gss-btn" disabled={!peutPrevisualiser} onClick={previsualiser}>
          {envoi ? 'Calcul…' : 'Prévisualiser l’impact'}
        </button>
      </div>

      {/* ── APERÇU : TROIS GROUPES, ET CEUX SANS SALAIRE À PART ───────────── */}
      {apercu && (
        <div className="gss-card" style={{ padding: 16, marginBottom: 16 }}>
          <strong>
            Aperçu — catégorie {apercu.code_cran} à {somme(apercu.montant, apercu.devise)}
            {' '}au 1er {moisAnnee(apercu.date_effet)}
          </strong>
          <div style={{ color: '#6B7280', fontSize: 13, margin: '4px 0 12px' }}>
            {apercu.note} Rien n’est écrit à ce stade.
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ background: '#FEF3C7', padding: 10, borderRadius: 6, minWidth: 170 }}>
              <div style={{ fontSize: 12, color: '#92400E' }}>Sous la nouvelle valeur</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{apercu.groupes.sous.nombre}</div>
              <div style={{ fontSize: 12, color: '#92400E' }}>seuls agents augmentés</div>
            </div>
            <div style={{ background: '#F3F4F6', padding: 10, borderRadius: 6, minWidth: 150 }}>
              <div style={{ fontSize: 12, color: '#374151' }}>Déjà à cette valeur</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{apercu.groupes.egaux.nombre}</div>
              <div style={{ fontSize: 12, color: '#374151' }}>inchangés</div>
            </div>
            <div style={{ background: '#DCFCE7', padding: 10, borderRadius: 6, minWidth: 150 }}>
              <div style={{ fontSize: 12, color: '#166534' }}>Au-dessus</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{apercu.groupes.au_dessus.nombre}</div>
              <div style={{ fontSize: 12, color: '#166534' }}>jamais baissés</div>
            </div>
            {/* ⚠️ À PART, et nommé. Ni sous, ni au-dessus : hors paie. */}
            {apercu.groupes.sans_salaire.nombre > 0 && (
              <div style={{ background: '#FEE2E2', padding: 10, borderRadius: 6, minWidth: 220 }}>
                <div style={{ fontSize: 12, color: '#991B1B' }}>Sans salaire de base</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{apercu.groupes.sans_salaire.nombre}</div>
                <div style={{ fontSize: 12, color: '#991B1B' }}>
                  {apercu.groupes.sans_salaire.agents.join(', ')} — <strong>à renseigner</strong> sur
                  la fiche. La paie les refuse ; cette révision ne les corrige pas.
                </div>
              </div>
            )}
          </div>

          {/* ── IMPACT, DANS LA DEVISE DU CLIENT ──────────────────────────── */}
          <div style={{ background: '#EFF6FF', padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 14 }}>
            Coût mensuel brut des <strong>{nbCoches}</strong> agent(s) retenu(s) :{' '}
            <strong>{somme(impactCoche, apercu.devise)}</strong>
            {tauxPat === null ? (
              /* ⚠️ On ne remplace JAMAIS un taux absent par celui d'un autre pays. */
              <div style={{ color: '#991B1B', marginTop: 4 }}>
                Charges patronales <strong>non chiffrées</strong> : le référentiel de ce pays
                ne porte aucun taux à cette date. Le coût affiché est le brut seul.
              </div>
            ) : (
              <div style={{ color: '#374151', marginTop: 4 }}>
                + charges patronales {tauxPat} % ={' '}
                <strong>{somme(Math.round(impactCoche * tauxPat / 100), apercu.devise)}</strong>
                {' '}— coût total{' '}
                <strong>{somme(impactCoche + Math.round(impactCoche * tauxPat / 100), apercu.devise)}</strong>
              </div>
            )}
          </div>

          {/* ── À QUI ON L'APPLIQUE ───────────────────────────────────────── */}
          {sous.length > 0 && (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                <strong style={{ fontSize: 14 }}>Agents à augmenter</strong>
                <button className="gss-btn" style={{ padding: '2px 8px', fontSize: 12 }}
                  onClick={() => { const c = {}; sous.forEach(a => { c[a.agent_id] = true; }); setCoches(c); }}>
                  Tout cocher
                </button>
                <button className="gss-btn" style={{ padding: '2px 8px', fontSize: 12 }}
                  onClick={() => setCoches({})}>Tout décocher</button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="gss-table" style={{ fontSize: 13, minWidth: 560 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>Appliquer</th>
                      <th>Matricule</th>
                      <th>Montant actuel</th>
                      <th>Montant après</th>
                      <th>Écart</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sous.map((a) => {
                      const coche = !!coches[a.agent_id];
                      return (
                        <tr key={a.agent_id} style={coche ? undefined : { color: '#9CA3AF' }}>
                          <td>
                            <input type="checkbox" checked={coche}
                              onChange={() => setCoches({ ...coches, [a.agent_id]: !coche })} />
                          </td>
                          <td>{a.matricule}</td>
                          <td>{somme(a.actuel, apercu.devise)}</td>
                          <td>{coche ? somme(apercu.montant, apercu.devise) : somme(a.actuel, apercu.devise)}</td>
                          <td>{coche ? `+ ${somme(apercu.montant - a.actuel, apercu.devise)}` : 'inchangé'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {nbDecoches > 0 && (
                /* ⚠️ On DIT ce que décocher écrit. Un agent décoché ne reste pas
                 *    « en dehors » : une exception individuelle datée le maintient
                 *    à son montant. Elle s'écrit, et elle se relit. */
                <div style={{ background: '#FEF3C7', color: '#92400E', padding: 10, borderRadius: 6, marginTop: 8, fontSize: 13 }}>
                  {nbDecoches} agent(s) décoché(s) : une <strong>exception individuelle datée</strong> sera
                  écrite pour chacun, à son montant actuel, dans la même opération. Sans elle, la
                  ligne de catégorie les augmenterait quand même.
                </div>
              )}
            </>
          )}

          {/* ── LE MOTIF, OBLIGATOIRE ─────────────────────────────────────── */}
          <div className="gss-field" style={{ marginTop: 14 }}>
            <label>Motif de la révision *</label>
            <input className="gss-input" value={motif} maxLength={500}
              placeholder="accord d’établissement, arrêté, décision de direction…"
              onChange={e => setMotif(e.target.value)} />
            <small style={{ color: '#6B7280' }}>
              Obligatoire : c’est ce que lira, dans deux ans, celui qui demandera pourquoi
              ce montant a changé ce mois-là.
            </small>
          </div>

          <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="gss-btn gss-btn-primary" disabled={!peutEcrire} onClick={appliquer}>
              {envoi ? 'Enregistrement…' : 'Enregistrer la révision'}
            </button>
            {!motif.trim() && (
              <small style={{ color: '#991B1B' }}>Le motif est obligatoire.</small>
            )}
          </div>
        </div>
      )}

      {/* ── COMPTE RENDU ──────────────────────────────────────────────────── */}
      {compteRendu && (
        <div className="gss-card" style={{ padding: 16, marginBottom: 16, background: '#DCFCE7' }}>
          {/* ⚠️ L ÉCRAN COMPOSE SA PHRASE ET FORMATE LUI-MÊME LES MONTANTS.
              🔴 Défaut vu par Dom le 01/10/2026 : ce bandeau affichait
                 « 50000.00 ZZD » — la prose du serveur interpolait le `numeric`
                 rendu par PostgreSQL, avec ses deux décimales et sans séparateur
                 de milliers, à deux lignes d'un tableau qui écrit « 50 000 ZZD ».
              ➡️ Un montant se formate à UN endroit, `somme()`, celui qui connaît
                 la devise du client. *Deux chemins d'affichage pour un même
                 montant finissent par en afficher deux.* `compteRendu.message`
                 reste rendu par l'API pour ses appelants et le journal, mais
                 l'écran n'en dépend plus. */}
          <strong>
            Catégorie {compteRendu.revision.code_cran} révisée à{' '}
            {somme(compteRendu.revision.montant, devise)} à compter de{' '}
            {moisAnnee(compteRendu.revision.date_effet)}.
          </strong>
          <div style={{ fontSize: 13, color: '#166534', marginTop: 6 }}>{compteRendu.portee}</div>
          {compteRendu.exceptions && compteRendu.exceptions.length > 0 && (
            <div style={{ fontSize: 13, marginTop: 8 }}>
              Exceptions individuelles écrites :{' '}
              {compteRendu.exceptions.map(e => `${e.matricule} (${somme(e.montant, devise)})`).join(' · ')}
            </div>
          )}
        </div>
      )}

      {/* ── HISTORIQUE : CE QUI A CHANGÉ, QUAND, ET DEPUIS QUELLE VALEUR ───
          ⚠️ « Valeur avant » se lit dans la grille elle-même, pas dans le
             journal d'audit : la grille EST l'historique des décisions. Les
             exceptions individuelles ont leur propre suite de valeurs, et ne
             sont pas mélangées à celle de leur catégorie. */}
      <div className="gss-card" style={{ padding: 16 }}>
        <strong>Historique des révisions</strong>
        {histo.length === 0 ? (
          <div style={{ color: '#6B7280', fontSize: 13, marginTop: 8 }}>
            Aucune révision enregistrée. La paie lit alors le montant porté par chaque fiche agent.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table className="gss-table" style={{ fontSize: 13, minWidth: 820 }}>
              <thead>
                <tr>
                  <th>Mois d’effet</th>
                  <th>Catégorie</th>
                  <th>Portée</th>
                  <th>Valeur avant</th>
                  <th>Valeur après</th>
                  <th>Motif</th>
                  <th>Texte de référence</th>
                  <th>Auteur</th>
                </tr>
              </thead>
              <tbody>
                {histo.map((r, i) => (
                  <tr key={`${r.code_cran}-${r.agent_id || 'cat'}-${r.date_effet}-${i}`}>
                    <td>{moisAnnee(r.date_effet)}</td>
                    <td>{r.code_cran}</td>
                    <td>{r.nature === 'exception' ? 'Exception individuelle' : 'Catégorie'}</td>
                    {/* La première ligne d'une suite n'a pas de valeur avant : on
                        écrit « première valeur », pas un tiret qui se lirait zéro. */}
                    <td>{r.valeur_avant === null ? 'première valeur' : somme(r.valeur_avant, devise)}</td>
                    <td><strong>{somme(r.valeur_apres, devise)}</strong></td>
                    <td>{r.motif}</td>
                    <td>{r.date_texte_reference || '—'}</td>
                    {/* ⚠️ NOM *ET* RÔLE. « Qui » sans « à quel titre » ne dit pas
                        si la personne pouvait engager l'employeur. Les deux se
                        lisent par jointure, donc restent justes après un
                        renommage de compte. */}
                    <td>
                      {r.auteur_nom || r.auteur_email}
                      {r.auteur_roles && (
                        <div style={{ color: '#6B7280', fontSize: 12 }}>{r.auteur_roles}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
